import { Progress } from '@nextui-org/react';
import axios, { AxiosError, CancelTokenSource } from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { FaFileAlt, FaTimesCircle } from 'react-icons/fa';
import styled from 'styled-components';
import { postUploadFast } from '../../../../redux/features/orders/ordersThunk';
import { useAsyncDispatch } from '../../../../redux/store';

const ItemFileUpload = ({
  file,
  handleRemove,
}: {
  file: File;
  handleRemove: () => void;
}): JSX.Element => {
  const [loading, setLoading] = useState<boolean>(false);
  const [response, setResponse] = useState<
    | {
        success: boolean;
        message: string;
      }
    | undefined
  >(undefined);
  const dispatch = useAsyncDispatch();
  const cancelToken = axios.CancelToken;
  const cancelTokenRef = useRef<CancelTokenSource>();

  const getBase64 = (): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.readAsDataURL(file);

      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(',')[1]; // Remove "data:text/plain;base64,"
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSendFile = async (): Promise<void> => {
    const source: CancelTokenSource = cancelToken.source();
    cancelTokenRef.current = source;
    setLoading(true);
    await dispatch(
      postUploadFast({
        body: {
          base64: await getBase64(),
          fileName: file.name,
        },
        cancelToken: source,
      })
    )
      .unwrap()
      .then(async (res) => {
        setResponse(res);
      })
      .catch((error) => {
        const axiosError = error as AxiosError;
        if (axiosError.code === 'ERR_CANCELED') return;
        setResponse(error.response.data);

        toast.error(
          error.response?.data?.mensagem || 'Erro ao realizar upload do arquivo'
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    handleSendFile();
  }, []);

  return (
    <Container>
      <div className="flex flex-row gap-4">
        <FaFileAlt size={20} className="text text-foreground-700" />
        <span className="text text-foreground-600">{file.name}</span>
      </div>
      <div className="flex w-full flex-row items-center justify-end gap-4">
        {response ? (
          <span className="text text-sm font-thin text-foreground-600">
            {response.message}
          </span>
        ) : (
          <Progress
            isIndeterminate={loading}
            className="flex max-w-md justify-center"
            size="sm"
          />
        )}
        {!loading && (
          <FaTimesCircle
            size={20}
            className="text cursor-pointer text-danger"
            onClick={handleRemove}
          />
        )}
      </div>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 12px;
  border: 1px solid #d4d4d4;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0px 2px 3px -2px rgba(0, 0, 0, 0.25);
  width: 100%;
`;

export default ItemFileUpload;
